import React, {
    useEffect, useState, useContext, useCallback
} from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { Loader, useModal } from '@jutro/components';
import { Wizard } from '@xengage/gw-portals-wizard-react';
import { ViewModelServiceContext, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { messages as platformMessages } from '@xengage/gw-platform-translations';
import { FormModalPopover } from 'ml-components-platform-react';
import { QuoteInfoComponentContext } from 'gw-capability-quoteandbind-common-react';
import { CustomQuoteInfoComponent } from 'ml-capability-quoteandbind-common-react';
import { CancellationService } from 'gw-capability-gateway';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import messages from './CancellationWizard.messages';
import wizardConfig from './config/cancellation-policychange-wizard-config.json5';


function CancellationWizard(props) {
    const modalApi = useModal();
    const { steps, title } = wizardConfig;
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const viewModelService = useContext(ViewModelServiceContext);
    const { location, history } = props;
    const { authHeader } = useAuthentication();
    const translator = useTranslator();

    const ModalService = MLModalService();
    useEffect( () => {
        if (!location.state) {
            history.push('/');
            return;
        }
        const {
            state: { cancellationData }
        } = location;
        if (!cancellationData) {
            return;
        }
        const submissionVM = viewModelService.create(
            cancellationData,
            'pc', 
            'edge.capabilities.gateway.job.cancellation.dto.CancellationDTO'
        );
        setInitialSubmission(submissionVM);
        setIsLoading(false);
        return () => {
            MLTagManagerService.setModel();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOnCancel = useCallback((wizardProps) => {
        const {currentStepIndex, wizardData, param: nextPath = ''} = wizardProps;
        const currentStep = steps[currentStepIndex].mlStepName;
        if(currentStep === 'cancellationConfirmation')  return true;
        if(nextPath.indexOf('cancellationContactUs') > -1)  return true;

        const componentProps = {
            title: translator(messages.cancelPromptTitle),
            actionBtnLabel: platformMessages.yes,
            cancelBtnLabel: platformMessages.cancelModel,
            simpleMessage: translator(messages.cancelPrompt),
            closeSimpleMessage: false
        };

        return modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then(() => {
            const jobNumber = _.get(wizardData, 'submissionVM.jobNumber.value');
            if(jobNumber){
                const promise = CancellationService.withdrawCancellationByCancellationNumber(jobNumber, authHeader).then(() => {
                    setIsLoading(true);
                    history.push(nextPath);
                    return true;
                }).catch((error) => {
                    error.modalTitle = translator(messages.withdrawErrorTitle);
                    error.modalMessage = translator(messages.withdrawErrorMessage);
                    throw error;
                });
                ModalService.showProgressDialog(messages.withdrawing, promise);
                return promise;
            }
            setIsLoading(true);
            history.push(nextPath);
            return true;
        }, _.noop);
        // US946510 avoid showing modal twice (withdraw error - no active period)
    }, []);

    const handleError = useCallback((error) => {
        console.error(error);
        const errorTitle = error.modalTitle ||translator(messages.genericErrorTitle);
        const errorMessage = error.modalMessage || translator(messages.genericErrorMessage);
        const componentProps = {
            title: translator(errorTitle),
            actionBtnLabel: platformMessages.ok,
            simpleMessage: translator(errorMessage),
            closeSimpleMessage: false
        };

        return modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then(() => {
            return history.push('/cancellationContactUs');
        }, _.noop);
    }, [history, translator]);


    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (!initialSubmission) {
        return null;
    }

    return (
        <QuoteInfoComponentContext.Provider value={CustomQuoteInfoComponent}>
            <ErrorBoundary onError={handleError}>
                <Wizard
                    initialSteps={steps}
                    wizardTitle={title}
                    initialData={{
                        submissionVM: initialSubmission,
                        isCancellation: true
                    }}
                    onCancel={handleOnCancel}
                    onPreviousModalProps={{
                        title: platformMessages.wantToJump,
                        message: platformMessages.wantToJumpMessage,
                        status: 'warning',
                        icon: 'mi-error-outline',
                        confirmButtonText: platformMessages.yes,
                        cancelButtonText: platformMessages.no
                    }}
                    extractVMObject={(data) => _.get(data, 'submissionVM')}
                />
            </ErrorBoundary>
        </QuoteInfoComponentContext.Provider>
    );
}

export default withRouter(withViewModelService(CancellationWizard));